import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Separator,
	Textarea,
} from "@monbits/ui";
import { ArrowRight, Check, Copy, Send } from "lucide-react";
import * as React from "react";
import {
	useNavigate,
	useOutletContext,
	useParams,
	useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { EntityDrawer } from "src/components/entity-drawer";
import TimeLabel from "src/components/time-label/time-label";
import type { BlockedWalletAsset } from "src/providers/fund-return-provider";
import { useCreateFundReturn } from "src/queries/fund-return-queries";
import type { FundReturnsOutletContext } from "./fund-returns-list";

const PARENT_PATH = "/fund-returns";

export const BlockedWalletDetail: React.FC = () => {
	const { walletId, assetSymbol } = useParams<{
		walletId: string;
		assetSymbol: string;
	}>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const outletContext = useOutletContext<
		FundReturnsOutletContext | undefined
	>();
	const blockedWallets = outletContext?.blockedWallets ?? [];
	const isLoadingWallets = outletContext?.isLoadingWallets ?? false;

	const walletIdValue = walletId ? Number.parseInt(walletId, 10) : null;

	const wallet = React.useMemo(() => {
		if (walletIdValue === null) return null;
		return blockedWallets.find((item) => item.id === walletIdValue) ?? null;
	}, [blockedWallets, walletIdValue]);

	const asset = React.useMemo<BlockedWalletAsset | null>(() => {
		if (!wallet || !assetSymbol) return null;
		return wallet.assets.find((item) => item.symbol === assetSymbol) ?? null;
	}, [wallet, assetSymbol]);

	const createMutation = useCreateFundReturn();

	const [showCreateDialog, setShowCreateDialog] = React.useState(false);
	const [destinationAddress, setDestinationAddress] = React.useState("");
	const [amount, setAmount] = React.useState("");
	const [reason, setReason] = React.useState("");
	const [totpCode, setTotpCode] = React.useState("");
	const [copiedAddress, setCopiedAddress] = React.useState<string | null>(null);

	const handleCopyAddress = (address: string) => {
		navigator.clipboard.writeText(address);
		setCopiedAddress(address);
		toast.success("Address copied");
		setTimeout(() => setCopiedAddress(null), 2000);
	};

	const handleOpenCreateDialog = () => {
		if (!asset) return;
		setAmount(asset.balance);
		setShowCreateDialog(true);
	};

	const handleCloseCreateDialog = () => {
		setShowCreateDialog(false);
		setDestinationAddress("");
		setAmount("");
		setReason("");
		setTotpCode("");
	};

	const handleCreate = async () => {
		if (!wallet || !asset) return;

		if (!destinationAddress.trim()) {
			toast.error("Destination address is required");
			return;
		}

		if (!amount.trim() || Number.parseFloat(amount) <= 0) {
			toast.error("Valid amount is required");
			return;
		}

		if (reason.trim().length < 10) {
			toast.error("Reason must be at least 10 characters");
			return;
		}

		if (totpCode.trim().length !== 6) {
			toast.error("Invalid 2FA code");
			return;
		}

		const ticker = asset.isNative
			? wallet.blockchain
			: `${wallet.blockchain}_${asset.symbol}`;

		try {
			await createMutation.mutateAsync({
				walletId: wallet.id,
				destinationAddress: destinationAddress.trim(),
				amount: amount.trim(),
				currency: ticker,
				tokenAddress: asset.tokenAddress,
				reason: reason.trim(),
				totpCode: totpCode.trim(),
			});
			toast.success("Fund return created successfully");
			handleCloseCreateDialog();

			const params = new URLSearchParams(searchParams);
			params.set("tab", "returns");
			const queryString = params.toString();
			navigate(queryString ? `${PARENT_PATH}?${queryString}` : PARENT_PATH);
		} catch (error: unknown) {
			console.error("Failed to create fund return:", error);
			const errorMessage =
				(error as { response?: { data?: { message?: string } } })?.response
					?.data?.message || "Failed to create fund return";
			toast.error(errorMessage);
		}
	};

	const isNotFound = !isLoadingWallets && (!wallet || !asset);

	return (
		<>
			<EntityDrawer
				title="Blocked Wallet Details"
				isLoading={isLoadingWallets}
				isNotFound={isNotFound}
				notFoundMessage="Blocked wallet not found"
				parentPath={PARENT_PATH}
			>
				{wallet && asset && (
					<div className="flex flex-col h-full">
						<div className="flex-1 overflow-y-auto p-6">
							<div className="space-y-6">
								<div className="space-y-4">
									<h4 className="text-sm font-medium text-muted-foreground">
										Wallet
									</h4>
									<div className="space-y-3">
										<div className="flex justify-between">
											<span className="text-sm text-muted-foreground">
												Network
											</span>
											<div className="flex items-center gap-2">
												<CryptoIcon
													ticker={wallet.blockchain}
													className="h-4 w-4"
												/>
												<span className="text-sm font-medium">
													{wallet.blockchain}
												</span>
											</div>
										</div>
										<div className="flex justify-between">
											<span className="text-sm text-muted-foreground">
												Address
											</span>
											<div className="flex items-center gap-1">
												<span className="font-mono text-sm">
													{wallet.address.slice(0, 10)}...
													{wallet.address.slice(-8)}
												</span>
												<Button
													variant="ghost"
													size="icon"
													className="h-6 w-6"
													onClick={() => handleCopyAddress(wallet.address)}
												>
													{copiedAddress === wallet.address ? (
														<Check className="h-3 w-3 text-green-600" />
													) : (
														<Copy className="h-3 w-3" />
													)}
												</Button>
											</div>
										</div>
										<div className="flex justify-between">
											<span className="text-sm text-muted-foreground">
												Block Reason
											</span>
											<span className="text-sm text-orange-600">
												{wallet.blockReason}
											</span>
										</div>
										<div className="flex justify-between">
											<span className="text-sm text-muted-foreground">
												Blocked At
											</span>
											<TimeLabel time={wallet.blockedAt} />
										</div>
									</div>
								</div>

								<Separator />

								<div className="space-y-4">
									<h4 className="text-sm font-medium text-muted-foreground">
										Asset
									</h4>
									<div className="space-y-3">
										<div className="flex justify-between">
											<span className="text-sm text-muted-foreground">
												Currency
											</span>
											<div className="flex items-center gap-2">
												<CryptoIcon ticker={asset.symbol} className="h-4 w-4" />
												<span className="text-sm font-medium">
													{asset.symbol}
												</span>
												{asset.isNative && (
													<span className="text-xs text-muted-foreground">
														(native)
													</span>
												)}
											</div>
										</div>
										<div className="flex justify-between">
											<span className="text-sm text-muted-foreground">
												Balance
											</span>
											<span className="font-mono text-sm font-medium">
												{asset.balance} {asset.symbol}
											</span>
										</div>
										{asset.tokenAddress && (
											<div className="flex justify-between">
												<span className="text-sm text-muted-foreground">
													Contract
												</span>
												<span className="font-mono text-xs">
													{asset.tokenAddress.slice(0, 10)}...
													{asset.tokenAddress.slice(-8)}
												</span>
											</div>
										)}
									</div>
								</div>

								<Separator />

								<div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950">
									<p className="text-sm text-yellow-800 dark:text-yellow-200">
										This wallet was blocked due to AML concerns. You can return
										the funds to an external address after verification.
									</p>
								</div>
							</div>
						</div>

						<Separator />

						<div className="p-4">
							<Button
								onClick={handleOpenCreateDialog}
								className="w-full"
								disabled={createMutation.isPending}
							>
								<ArrowRight className="mr-2 h-4 w-4" />
								Return Funds
							</Button>
						</div>
					</div>
				)}
			</EntityDrawer>

			<Dialog
				open={showCreateDialog}
				onOpenChange={(open) => !open && handleCloseCreateDialog()}
			>
				<DialogContent className="sm:max-w-[500px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Send className="h-5 w-5" />
							Create Fund Return
						</DialogTitle>
						<DialogDescription>
							Return funds to an external address. This requires 2FA
							verification.
						</DialogDescription>
					</DialogHeader>

					{wallet && asset && (
						<div className="space-y-4 py-4">
							<div className="rounded-lg border p-3 bg-muted/50">
								<div className="space-y-2 text-sm">
									<div className="flex justify-between">
										<span className="text-muted-foreground">Asset</span>
										<span className="font-medium">
											{asset.balance} {asset.symbol}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">From</span>
										<span className="font-mono text-xs">
											{wallet.address.slice(0, 12)}...
										</span>
									</div>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="destination">Destination Address</Label>
								<Input
									id="destination"
									placeholder="Enter destination address"
									value={destinationAddress}
									onChange={(e) => setDestinationAddress(e.target.value)}
									disabled={createMutation.isPending}
								/>
							</div>

							<div className="space-y-2">
								<Label htmlFor="amount">Amount ({asset.symbol})</Label>
								<Input
									id="amount"
									type="text"
									value={amount}
									onChange={(e) => setAmount(e.target.value)}
									disabled={createMutation.isPending}
								/>
								<p className="text-xs text-muted-foreground">
									Max: {asset.balance}
								</p>
							</div>

							<div className="space-y-2">
								<Label htmlFor="reason">Reason</Label>
								<Textarea
									id="reason"
									placeholder="Reason for fund return (min 10 characters)"
									value={reason}
									onChange={(e) => setReason(e.target.value)}
									disabled={createMutation.isPending}
									className="min-h-[80px]"
								/>
							</div>

							<div className="space-y-2">
								<Label htmlFor="totp">2FA Code</Label>
								<Input
									id="totp"
									placeholder="6-digit code"
									value={totpCode}
									onChange={(e) =>
										setTotpCode(e.target.value.replace(/\D/g, ""))
									}
									disabled={createMutation.isPending}
									maxLength={6}
									inputMode="numeric"
								/>
							</div>
						</div>
					)}

					<DialogFooter>
						<Button
							variant="outline"
							onClick={handleCloseCreateDialog}
							disabled={createMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							onClick={handleCreate}
							disabled={
								createMutation.isPending ||
								!destinationAddress.trim() ||
								!amount.trim() ||
								reason.trim().length < 10 ||
								totpCode.length !== 6
							}
						>
							{createMutation.isPending ? "Creating..." : "Create"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};
